"use client";

import Image from "next/image";
import { useState } from "react";

const avatarSizes = {
  xs: { className: "h-[18px] w-[18px] text-[7px]", pixels: 18 },
  sm: { className: "h-7 w-7 text-[10px]", pixels: 28 },
  md: { className: "h-10 w-10 text-xs", pixels: 40 },
  lg: { className: "h-14 w-14 text-base", pixels: 56 },
} as const;

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toLocaleUpperCase() ?? "")
    .join("") || "?";
}

export function ClientAvatar({
  name,
  color,
  photoUrl,
  size = "md",
  className = "",
}: {
  name: string;
  color: string;
  photoUrl?: string | null;
  size?: keyof typeof avatarSizes;
  className?: string;
}) {
  const config = avatarSizes[size];
  const [failedPhoto, setFailedPhoto] = useState<string | null>(null);
  const showPhoto = Boolean(photoUrl) && failedPhoto !== photoUrl;

  return (
    <span
      className={`relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold text-white ${config.className} ${className}`}
      style={{ backgroundColor: color, boxShadow: `0 0 0 2px ${color}45` }}
      title={name}
      aria-hidden="true"
    >
      {showPhoto && photoUrl ? (
        <Image
          src={photoUrl}
          alt=""
          width={config.pixels}
          height={config.pixels}
          sizes={`${config.pixels}px`}
          className="h-full w-full object-cover"
          unoptimized
          onError={() => setFailedPhoto(photoUrl)}
        />
      ) : initials(name)}
      <span
        className="pointer-events-none absolute inset-x-0 bottom-0 h-1/4"
        style={{ backgroundColor: color }}
      />
    </span>
  );
}
